import type { IncomeTestsRuleSet } from "./types.js";

// income-tests ruleset for FY2025-26 (1 July 2025 – 30 June 2026).
//
// The only FY-varying figures here are the FBT rate (which fixes the family-
// assistance un-gross-up factor) and the RFBA reporting threshold. The income
// derivations themselves are structural (which components each test adds) and
// live in calculate.ts.
//
// FBT rate (BUILD-CRITICAL): the applicable FBT rate is 47% for the FBT years
// ending 31 March 2023 through 31 March 2027 (ATO "Fringe benefits tax rates
// and thresholds"). So the family-assistance factor is:
//
//   exemptEmployerAtiFactor = 1 − 0.47 = 0.53   (NOT 0.535)
//
// 0.535 = (1 − 0.465) reflects the OLD 46.5% FBT rate (FBT 2015-16/2016-17 only)
// and is STALE. Shipping 0.535 would understate ATI's fringe-benefit component
// — a misleading-conduct accuracy risk. The 0.53 un-gross-up applies ONLY to
// the s57A exempt-employer RFBA portion; non-exempt RFBA is counted at full
// grossed-up value even in ATI (see calculate.ts).
//
// Never edit a shipped ruleset to "fix" a later year — when the FBT rate
// changes after 31 March 2027, author a new fyXXXX-XX.ts and register it.
export const fy2025_26: IncomeTestsRuleSet = {
  product: "SHARED",
  fy: "2025-26",
  effectiveFrom: "2025-07-01",
  effectiveTo: "2026-06-30",
  sources: {
    fbtRates:
      "https://www.ato.gov.au/tax-rates-and-codes/fringe-benefits-tax-rates-and-thresholds",
    adjustedFringeBenefits:
      "https://guides.dss.gov.au/family-assistance-guide/3/2/3",
    adjustedTaxableIncome:
      "https://www.servicesaustralia.gov.au/what-adjusted-taxable-income",
    incomeForSurchargePurposes:
      "https://www.ato.gov.au/individuals-and-families/medicare-and-private-health-insurance/medicare-levy-surcharge/medicare-levy-surcharge-income-thresholds-and-rates",
    helpRepaymentIncome:
      "https://www.ato.gov.au/individuals-and-families/income-deductions-offsets-and-records/income-tests",
    rfbaConsequences:
      "https://www.ato.gov.au/individuals-and-families/jobs-and-employment-types/working-as-an-employee/reportable-fringe-benefits-for-employees/consequences-of-having-a-reportable-fringe-benefits-amount",
    rfbaReporting:
      "https://www.ato.gov.au/businesses-and-organisations/hiring-and-paying-your-workers/fringe-benefits-tax/fbt-registration-lodgment-payment-and-reporting/reportable-fringe-benefits",
    fbtExemptCaps:
      "https://www.ato.gov.au/businesses-and-organisations/hiring-and-paying-your-workers/fringe-benefits-tax/fbt-concessions-for-not-for-profit-organisations/fbt-exempt-organisations",
  },
  sourcesVerifiedAt: "2026-06-22",

  // FBT rate 47% for FBT years to 31 Mar 2027 → factor 0.53.
  fbtRate: 0.47,
  exemptEmployerAtiFactor: 1 - 0.47, // = 0.53, explicitly derived from fbtRate.

  // An RFBA is reported where the TAXABLE value of benefits exceeds $2,000 in
  // the FBT year; the reported amount is then the grossed-up figure.
  rfbaReportingThreshold: 2_000,

  // Grossed-up FBT-exempt packaging caps (per employee per FBT year). These are
  // the STATUTORY grossed-up caps — distinct from the ~$9,010/$15,900 net cash
  // packaging limits providers advertise. Held for reference only.
  fbtExemptCapsGrossedUp: {
    pbiHpc: 30_000,
    hospitalAmbulance: 17_000,
    mealEntertainment: 5_000,
  },
};
